import React, { useEffect, useMemo, useState } from 'react';
import ConfirmDialog from '../ConfirmDialog';
import { useDateFormat, useTimeFormat } from '../../context/LocaleContext';
import {
  CHANNELS, AUDIENCES, CANCELLATION_NOTE_MAX, cancellationRecipients,
} from '../../domain/notifications';
import { useSmsAvailable } from '../../context/SmsGateContext';
import { smsGatedChoice } from '../../domain/smsGate';
import SmsUnavailable, { smsOffClass } from '../SmsUnavailable';
import useNames from '../../hooks/useNames';
import { SCOPE_WORDING, NOTICE_WORDING, eventSubtitle } from '../../domain/calendarWording';
import s from './RecurringScopeDialog.module.css';

// "You touched one lesson in a repeating series — did you mean this one, or
// this one onwards?" As in MMS, which asks exactly this and offers exactly
// these two answers.
//
// No "all entries": changing a series retroactively would rewrite lessons
// already taught and, through attendance, already charged.
//
// Deleting asks the same question, because it is the same ambiguity. MMS says
// "entry/entries" when updating and "event/events" when deleting, and only the
// delete dialog carries the cancellation notices.
//
// ── MMS's Delete Event dialog, top to bottom ───────────────────────────────
//   Delete Event
//   (•) This event
//       Lesson scheduled for 9/12/2026 at 4:00 PM     <- subtitle, first radio only
//   ( ) This and following events
//   [ ] Send email cancellation notices
//         [x] Email students          <- both children default checked
//         [x] Email parents
//         (amber box: who has no email address on file)
//   [ ] Send SMS cancellation notices
//         [x] SMS students            <- "SMS", not "Text"
//         [x] SMS parents
//         (amber box: who has no SMS capable number on file)
//   Cancellation message
//   [                                        ]
//   Optional
//   [Cancel] [Delete]
//
// The channel checkbox is tri-state (unticking one child leaves it
// indeterminate); each channel has its own warning box beneath its own
// children; and the box recomputes live as the children change.
//
// `addressOn` in domain/notifications.js reads `smsCapable` as part of the SMS
// address, so a family with a number on file but no tick is listed in the
// warning box rather than texted: a number on file is not consent to text it.

export const SCOPES = { one: 'one', following: 'following' };

// The words are `SCOPE_WORDING` in domain/calendarWording.js, so a phone's
// dialog says the same.
//
// `recurrence`: a recurrence change is not a scope question. MMS shows this
// instead when the frequency, the interval or the Repeat Until changes —
// different title, a sentence rather than radios, and a single Update — from
// the series' first occurrence or a middle one: one entry of a series cannot
// repeat every other week.
//
// `delete.notices`: who to tell. Unchecked by default, as MMS has them:
// telling a family a lesson is cancelled is a thing to decide, not a thing to
// opt out of.
const WORDING = SCOPE_WORDING;

// The two channels, and the words MMS puts on each of their controls. Every
// render below is a map over this array. "SMS students" / "SMS parents" is
// MMS's own wording, not "Text students".
const NOTICE_CHANNELS = [
  // The warning is verbatim from MMS. It names the recipients and says, in the
  // same breath, that the delete is not blocked.
  { channel: CHANNELS.email, ...NOTICE_WORDING.email },
  // "no SMS capable phone number on file", not "no phone number": MMS lists a
  // family who has a number not marked SMS-capable, and so do we. `addressOn`
  // is the single rule behind both the addressing and this warning.
  { channel: CHANNELS.sms, ...NOTICE_WORDING.sms },
];

// Both children ticked, as MMS opens them. The channel itself starts off.
const CLOSED = { open: false, students: false, parents: false };
const OPENED = { open: true, students: true, parents: true };

// Derived from the channel list, so a channel cannot be rendered and missing
// here, which would leave `notices[channel]` undefined on its first click.
const emptyNotices = () => Object.fromEntries(
  NOTICE_CHANNELS.map(({ channel }) => [channel, { ...CLOSED }]),
);

// The line under "This event": "Lesson scheduled for 9/12/2026 at 4:00 PM",
// the leading word the event's category. The sentence is `eventSubtitle` in
// domain/calendarWording.js; the date is formatted here, in the Region's
// pattern (src/domain/locale.js), by substituting into the ISO string rather
// than parsing it (a date-only value through local time lands a day early west
// of Greenwich). `detail.zoneLabel` is the series' zone abbreviation when the
// studio has moved zones, and empty otherwise (screenZoneLabel in
// domain/timeZone.js): this is the last line read before a lesson goes.

// A checkbox whose third state is a DOM property rather than an attribute, so
// React cannot set it from JSX and it needs a ref.
function TriStateCheckbox({ checked, indeterminate, onChange, label, className, disabled = false }) {
  return (
    <label className={disabled ? `${className} ${smsOffClass}` : className}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        ref={(el) => { if (el) el.indeterminate = Boolean(indeterminate && !checked); }}
        onChange={onChange}
      />
      <span>{label}</span>
    </label>
  );
}

// `scopes` is false wherever there is no ambiguity to resolve, and MMS drops
// the radios in exactly those cases: an event with only one entry to delete —
// which still gets this dialog, for the cancellation notices — and a
// recurrence change, which says what it will do and asks only whether.
//
// `noticeGroups` is one entry per family the delete would notify:
// { familyId, family, students }. Passed in so the dialog stays a pure function
// of its props, and the addresses the warning is computed from are the ones
// the page queues against.
//
// `money` is one entry per scope — `{ one, following }` — each the sentences
// `removalSentences` produced for what that scope would strand, or null.
// Keyed by scope because the figure changes with the radio. Sentences rather
// than a footprint, so what this says and what the page writes cannot drift
// apart.
//
// No money checkbox: deleting a lesson always retracts what taking attendance
// produced, as MMS does silently (see domain/lessonRemoval.js). Unlike MMS this
// dialog states what the lesson carried before it goes.
export default function RecurringScopeDialog({
  open, onCancel, onConfirm, action = 'update', canNotify = true, scopes = true,
  noticeGroups = [], detail = null, money = null,
}) {
  // Defaults to the narrow choice, as MMS does: too little is one more edit,
  // too much is a rewritten schedule or lessons removed that nobody asked to
  // remove.
  const names = useNames();
  const [scope, setScope] = useState(SCOPES.one);
  const shortDate = useDateFormat();
  // The start time on the studio's clock, `16:00` in Germany (#534).
  const clock = useTimeFormat();
  const [notices, setNotices] = useState(emptyNotices);
  // Whether the SMS channel can be taken at all (#782, domain/smsGate.js).
  const smsAvailable = useSmsAvailable();
  const [note, setNote] = useState('');
  // Dismissed warnings, per channel. MMS puts a × on each box.
  const [dismissed, setDismissed] = useState({});

  const words = WORDING[action] || WORDING.update;
  const showNotices = Boolean(words.notices && canNotify);

  // Reopened means asked afresh: the dialog is mounted permanently, so without
  // this the next delete would inherit the last one's ticks and the
  // cancellation message typed for a different family.
  useEffect(() => {
    if (!open) return;
    setScope(SCOPES.one);
    setNotices(emptyNotices());
    setNote('');
    setDismissed({});
  }, [open]);

  const setChannel = (channel, next) => {
    setNotices((n) => ({ ...n, [channel]: { ...n[channel], ...next } }));
    // The warning is about a specific set of ticks; changing them clears the
    // dismissal.
    setDismissed((d) => ({ ...d, [channel]: false }));
  };

  // Who would be written to and who cannot be reached, per channel, from the
  // current ticks — derived, and from the same domain function that resolves
  // the addresses the notices are queued to, so the box and the send cannot
  // disagree.
  const resolved = useMemo(() => {
    const none = { notices: [], unreachable: [] };
    if (!showNotices) return {};
    return Object.fromEntries(NOTICE_CHANNELS.map(({ channel }) => {
      const chosen = notices[channel];
      if (!chosen.open) return [channel, none];
      // nameFormats, so the "no address on file" list is written as every other
      // list in the app is.
      return [channel, cancellationRecipients(noticeGroups, {
        channel, audience: chosen, nameFormats: names.formats,
      })];
    }));
  }, [showNotices, notices, noticeGroups, names]);

  // What the confirm hands back: for each channel, the chosen audiences or null
  // if that channel sends nothing, plus the shared cancellation message. Every
  // channel appears, named, because this object is the interface to
  // `cancellationRequests`, which skips a null channel. The SMS half is null
  // while this deployment cannot send a text (#782): `smsGatedChoice` drops it
  // whatever the ticks say, since the gate can close under an open dialog.
  const chosenNotices = () => {
    const pick = (channel) => {
      const c = notices[channel];
      if (!c) return null;
      // Open but with both children unticked sends nothing: treated as off, so
      // a ticked channel can never queue a message with nobody on it.
      if (!c.open || (!c.students && !c.parents)) return null;
      return { [AUDIENCES.students]: c.students, [AUDIENCES.parents]: c.parents };
    };
    if (!showNotices) return { [CHANNELS.email]: null, [CHANNELS.sms]: null, note: '' };
    return smsGatedChoice(
      { [CHANNELS.email]: pick(CHANNELS.email), [CHANNELS.sms]: pick(CHANNELS.sms), note },
      smsAvailable,
    );
  };

  const subtitle = eventSubtitle(
    detail, detail?.date ? shortDate(detail.date) : '', detail?.startTime ? clock(detail.startTime) : '',
  );

  // Null wherever the chosen scope strands nothing, which is every ordinary
  // delete of an unmarked lesson. The block is absent then rather than present
  // and empty, so it is read when it says something.
  const moneyLines = money?.[scope] || null;

  return (
    <ConfirmDialog
      open={open}
      title={words.title}
      message={(
        <span>
          {/* A dialog that asks nothing has to say something instead: with the
              radios gone the message would otherwise be an empty box above two
              buttons. */}
          {words.body ? <span className={s.body}>{words.body}</span> : null}

          {(scopes ? [[SCOPES.one, words.one], [SCOPES.following, words.following]] : []).map(([value, label]) => (
            <label key={value} className={s.choice}>
              <input
                type="radio"
                name="recurring-scope"
                checked={scope === value}
                onChange={() => setScope(value)}
              />
              <span>
                {label}
                {/* Only the first radio carries it, and only on a delete: MMS
                    names the one event you are about to remove, because "this
                    event" on its own does not tell you which one you clicked. */}
                {value === SCOPES.one && subtitle
                  ? <span className={s.subtitle}>{subtitle}</span>
                  : null}
              </span>
            </label>
          ))}

          {/* What these lessons have already billed, and what deleting them
              does about it (#157): deleting always retracts a marked lesson's
              billing (domain/lessonRemoval.js), and this states it. Above the
              notices, because it is about the lesson, and before the Delete
              button. */}
          {moneyLines ? (
            <span className={s.money} role="status">
              <span className={s.moneySummary}>{moneyLines.summary}</span>
              <span className={s.moneyConsequence}>{moneyLines.consequence}</span>
            </span>
          ) : null}

          {showNotices ? (
            <span className={scopes ? s.notices : undefined}>
              {NOTICE_CHANNELS.map((c) => {
                // The SMS channel while texts cannot be sent (#782): disabled,
                // drawn closed whatever the ticks hold, with its one line under
                // it in place of the audiences.
                const gated = c.channel === CHANNELS.sms && !smsAvailable;
                const chosen = gated ? CLOSED : notices[c.channel];
                const missing = resolved[c.channel]?.unreachable || [];
                const bothOn = chosen.students && chosen.parents;
                return (
                  <span key={c.channel} className={s.channel}>
                    <TriStateCheckbox
                      className={s.choice}
                      label={c.label}
                      disabled={gated}
                      checked={chosen.open && bothOn}
                      // Unticking one child leaves the parent in the dash state,
                      // as MMS does.
                      indeterminate={chosen.open && (chosen.students !== chosen.parents)}
                      // Anything selected, clicking clears it and collapses;
                      // nothing selected, clicking opens with both ticked. Keyed
                      // on the children rather than on `open`, so a channel left
                      // expanded with both children unticked still turns back on
                      // when clicked.
                      onChange={() => {
                        if (gated) return;
                        setChannel(c.channel, (chosen.students || chosen.parents) ? CLOSED : OPENED);
                      }}
                    />

                    {gated ? <SmsUnavailable /> : null}

                    {chosen.open ? (
                      <span className={s.audience}>
                        {[[AUDIENCES.students, c.students], [AUDIENCES.parents, c.parents]].map(([key, label]) => (
                          <label key={key} className={s.choice}>
                            <input
                              type="checkbox"
                              checked={chosen[key]}
                              onChange={(e) => setChannel(c.channel, { [key]: e.target.checked })}
                            />
                            <span>{label}</span>
                          </label>
                        ))}

                        {missing.length && !dismissed[c.channel] ? (
                          <span className={s.warning} role="status">
                            <button
                              type="button"
                              className={s.dismiss}
                              aria-label="Dismiss"
                              onClick={() => setDismissed((d) => ({ ...d, [c.channel]: true }))}
                            >
                              ×
                            </button>
                            <span className={s.warningText}>{c.warning}</span>
                            {/* In the studio's Naming Format, as every other
                                list; MMS's warning uses the account's Student
                                Name Format too. */}
                            {missing.map((r) => (
                              <span key={`${r.key}`} className={s.missing}>{r.listName}</span>
                            ))}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </span>
                );
              })}

              {/* One message for both channels, not one per channel: MMS puts a
                  single Cancellation message below the lot, and it is the
                  %Note% its Event Cancelled template interpolates. */}
              <span className={s.messageField}>
                <span className={s.messageLabel} id="cancellation-message-label">Cancellation message</span>
                <textarea
                  className={s.messageInput}
                  aria-labelledby="cancellation-message-label"
                  rows={3}
                  maxLength={CANCELLATION_NOTE_MAX}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <span className={s.hint}>Optional</span>
              </span>

            </span>
          ) : null}
        </span>
      )}
      confirmLabel={words.confirm}
      cancelLabel=""
      discardLabel="Cancel"
      onDiscard={onCancel}
      onCancel={onCancel}
      // Delete is never disabled by the warning: MMS says so in the warning's
      // own words, and a teacher who cannot reach a family still has to be able
      // to remove the lesson. The page decides what to retract from the same
      // footprint that produced `moneyLines`.
      onConfirm={() => onConfirm(scope, chosenNotices())}
    />
  );
}
